"use client";

/**
 * Header currency switch.
 *
 * A small flag + code button (e.g. a Canada flag + "CAD") placed in the
 * top-right of the marketing header, next to Sign In. Clicking it opens a tiny
 * two-item popover to pick USD or CAD. It uses the same useCurrency hook as the
 * first-visit modal and the pricing section, so every surface stays in sync.
 *
 * The `scrolled` prop mirrors the Navbar's transparent-over-hero vs solid
 * scrolled states so the control is legible in both.
 */

import { useEffect, useRef, useState } from "react";
import { useCurrency } from "@/lib/useCurrency";
import type { Currency } from "@/lib/constants/pricing-display";
import { CurrencyFlag, CanadaFlag, USFlag } from "@/components/CurrencyFlags";

export default function CurrencySwitch({ scrolled }: { scrolled: boolean }) {
  const { currency, setCurrency } = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const textColor = scrolled ? "var(--color-text)" : "rgba(255,255,255,0.9)";
  const borderColor = scrolled ? "var(--color-border)" : "rgba(255,255,255,0.35)";

  const options: Array<{ currency: Currency; label: string; Flag: typeof CanadaFlag }> = [
    { currency: "CAD", label: "Canada · CAD", Flag: CanadaFlag },
    { currency: "USD", label: "United States · USD", Flag: USFlag },
  ];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Currency: ${currency}. Change currency.`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 10px",
          borderRadius: 999,
          cursor: "pointer",
          background: "transparent",
          border: `1px solid ${borderColor}`,
          fontFamily: "var(--font-body)",
          fontSize: "0.82rem",
          fontWeight: 500,
          color: textColor,
          transition: "color 0.3s, border-color 0.3s",
        }}
      >
        <CurrencyFlag currency={currency} size={18} />
        <span>{currency}</span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            minWidth: 190,
            background: "var(--color-bg)",
            border: "1px solid var(--color-border)",
            borderRadius: 12,
            padding: 6,
            boxShadow: "0 16px 40px rgba(0,0,0,0.14)",
            zIndex: 200,
          }}
        >
          {options.map(({ currency: c, label, Flag }) => {
            const active = c === currency;
            return (
              <button
                key={c}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  setCurrency(c);
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  cursor: "pointer",
                  textAlign: "left",
                  background: active ? "var(--color-bg-alt)" : "transparent",
                  border: "none",
                  fontFamily: "var(--font-body)",
                  fontSize: "0.85rem",
                  fontWeight: active ? 600 : 400,
                  color: "var(--color-text)",
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = "var(--color-bg-alt)";
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = "transparent";
                }}
              >
                <Flag size={20} />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
